import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import KernelRunsPage from "@/components/shared/KernelRunsPage";
import { lazy, Suspense, useEffect, useMemo, type ReactNode } from "react";
import { Navigate, Route, useLocation } from "react-router";
import { Spin } from "antd";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ATTENTION_QUERY_KEY } from "@/hooks/useAttentionCounts";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { logger } from "@/utils/logger";

/**
 * ADMIN-ONLY gate (PO 2026-09-16; renamed 2026-09-22 when Customer credits joined it).
 *
 * WHY A ROUTE GUARD AND NOT JUST THE SIDEBAR FLAG: `adminOnly` on the nav group
 * only stops us RENDERING THE LINK. The URL still resolved for anyone who typed
 * it or followed an old bookmark — a hidden door is not a locked one. The Harness
 * and the credit ledger are both operator surfaces, so the refusal lives on the route.
 *
 * The name says ADMIN rather than HARNESS because it guards more than the Harness now: two names
 * for one check is how a second, subtly different check gets written next time.
 *
 * The redirect target is the same one the app already uses when there is nothing
 * to show (see the dashboard fallback below), so a non-admin lands somewhere real
 * rather than on a dead end.
 */

  /**
   * KEEPS THE QUERY STRING WHEN AN OLD URL IS RENAMED (P080 D-125, 2026-09-21).
   * `/investor-outreach?entity=<id>` is a REAL deep link — `EntityDetailPage` navigates to it with
   * the company already chosen. `<Navigate to="/outreach">` would silently DROP the `?entity=`,
   * landing the person on the all-companies board instead of the company they clicked, and nothing
   * would error. A rename may lose the NAME, never the state the old URL carried.
   */
  function RedirectKeepingQuery({ to }: { to: string }) {
    const location = useLocation();
    return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
  }
function AdminOnlyRoute({ children }: { children: ReactNode }) {
  if (!isAdministrator()) return <Navigate to="/outreach" replace />;
  return <>{children}</>;
}
/**
 * THE ADMIN-MODE GUARD — the fence around the `/admin/*` namespace (P206 D-206-005 / D-206-009).
 *
 * `AdminOnlyRoute` above asks "IS THIS IDENTITY AN ADMINISTRATOR?" and keeps that rule on purpose:
 * it guards `/harness/*` and `/customer-credits`, which are the administrator's OWN workspace — a
 * CEO who switches to user mode to see what his customers see must not lock himself out of the
 * desk he works at all day (recorded in P127, where the mode check was wrongly used there).
 *
 * This one asks the OTHER question: "SHOULD ADMIN SURFACES SHOW RIGHT NOW?" — the switch at the top
 * right. It exists because `/settings` had NO guard at all: a regular user who typed the URL landed
 * in the taxonomy editor that the sidebar had correctly hidden from him. Hiding a link is not
 * locking a door, and the two controls must answer to ONE switch.
 *
 * A visitor whose mode does not allow the surface is REDIRECTED to what his mode does allow, never
 * shown a refusal page (D-206-009) — a "you are not allowed" screen teaches the person that the
 * product has doors he may not open.
 */
function AdminModeRoute({ children }: { children: ReactNode }) {
  if (!useAdminAccess()) return <Navigate to="/outreach" replace />;
  return <>{children}</>;
}
import { isAdministrator, useAdminAccess } from "@/providers/firebase/authProvider";
import { RaisyLoadingPage } from "@/components/loading/RaisyLoadingPage";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { RequestAccessPage } from "../login/RequestAccessPage";
// R2-24 — the locked-channel sentence, shared by the LinkedIn and WhatsApp routes.
import { ChannelComingSoon } from "../../mail/ChannelComingSoon";
// Lazy-load heavy pages for code-splitting
const AutomationPage = lazy(() => import("../pages/AutomationPage").then(m => ({ default: m.AutomationPage })));
const AnalyticsPage = lazy(() => import("../pages/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })));
const CampaignsPage = lazy(() => import("../pages/CampaignsPage").then(m => ({ default: m.CampaignsPage })));
const EmailPage = lazy(() => import("../pages/EmailPage").then(m => ({ default: m.EmailPage })));
// R2-24 — LINKEDIN AND WHATSAPP BOARDS ARE UNMOUNTED, not merely unlinked (PO 2026-10-03: *"just
// lock it up for now, same also for whatsapp. coming soon or something. BUT YEAH THIS CANT HAPPEN.
// this must load in milliseconds."*). Their two lazy chunks are gone from the graph entirely: the
// boards are what opened the socket that failed, painted "The … engine is not answering" and
// reloaded the app on top of it, and no amount of link-disabling prevents a bookmarked URL, a back
// button or the integrations page's own link from mounting them. The routes now render the same
// "coming soon" statement the switcher's chip shows, and nothing on that path talks to an engine.
const TemplatesPage = lazy(() => import("../pages/TemplatesPage").then(m => ({ default: m.TemplatesPage })));
const SegmentsPage = lazy(() => import("../pages/SegmentsPage").then(m => ({ default: m.SegmentsPage })));
const ListsPage = lazy(() => import("../pages/ListsPage").then(m => ({ default: m.ListsPage })));
const WorkflowsPage = lazy(() => import("../pages/WorkflowsPage").then(m => ({ default: m.WorkflowsPage })));
const JourneysPage = lazy(() => import("../pages/JourneysPage").then(m => ({ default: m.JourneysPage })));
// project139 placeholder — Pillar 3 Meetings surface (sidebar IA only for now).
const MeetingsPage = lazy(() => import("../pages/MeetingsPage").then(m => ({ default: m.MeetingsPage })));
// Pillar 4's honest placeholder — the fourth link made visible without pretending it is built.
const ContractsPage = lazy(() => import("../pages/ContractsPage").then(m => ({ default: m.ContractsPage })));

// Phase 2: AI Agent pages
const AgentHubPage = lazy(() => import("../pages/AgentHubPage").then(m => ({ default: m.AgentHubPage })));
const AgentConfigPage = lazy(() => import("../pages/AgentConfigPage").then(m => ({ default: m.AgentConfigPage })));
const AgentActivityPage = lazy(() => import("../pages/AgentActivityPage").then(m => ({ default: m.AgentActivityPage })));
const ProposalsPage = lazy(() => import("../pages/ProposalsPage").then(m => ({ default: m.ProposalsPage })));
const ProcessManagerPage = lazy(() => import("../pages/ProcessManagerPage"));
const ProcessBuilderPage = lazy(() => import("../pages/ProcessBuilderPage"));
const EntityListPage = lazy(() => import("../../entity/EntityListPage").then(m => ({ default: m.EntityListPage })));
const EntityDetailPage = lazy(() => import("../../entity/EntityDetailPage").then(m => ({ default: m.EntityDetailPage })));
// CRM sidebar "Contacts"/"Companies" — real raisy_BE entity data (see entityApi), not the
// fakerest demo resources below. These custom routes take precedence over the resources.
const ContactsPage = lazy(() => import("../pages/ContactsPage").then(m => ({ default: m.ContactsPage })));
const CompaniesPage = lazy(() => import("../pages/CompaniesPage").then(m => ({ default: m.CompaniesPage })));
// The ONE CRM surface (PO 2026-09-21) — both types, external half only. Replaces the two
// sidebar rows; the two pages above stay mounted so `/contacts` and `/companies` keep working.
const CrmPage = lazy(() => import("../pages/CrmPage").then(m => ({ default: m.CrmPage })));
const KernelSemanticSearchPage = lazy(() => import("../pages/KernelSemanticSearchPage").then(m => ({ default: m.KernelSemanticSearchPage })));
const KernelExactMatchPage = lazy(() => import("../pages/KernelExactMatchPage").then(m => ({ default: m.KernelExactMatchPage })));
const EntitySemanticSearchPage = lazy(() => import("../pages/EntitySemanticSearchPage").then(m => ({ default: m.EntitySemanticSearchPage })));
const EntityExactMatchPage = lazy(() => import("../pages/EntityExactMatchPage").then(m => ({ default: m.EntityExactMatchPage })));
// @P091 T0112 — the ADMIN view of one customer's wallet: balance, plan, price per email, every
// movement that produced the balance, and an adjustment that carries a reason (D-103).
const CustomerCreditsPage = lazy(() => import("../pages/CustomerCreditsPage").then(m => ({ default: m.CustomerCreditsPage })));

// Phase 3: Feedback pages
const FeedbackQueuePage = lazy(() => import("../pages/FeedbackQueuePage").then(m => ({ default: m.FeedbackQueuePage })));
const FeedbackHistoryPage = lazy(() => import("../pages/FeedbackHistoryPage").then(m => ({ default: m.FeedbackHistoryPage })));

// Phase 4: Data & Pipeline pages
const ProspectingPage = lazy(() => import("../pages/ProspectingPage").then(m => ({ default: m.ProspectingPage })));
const EnrichmentPage = lazy(() => import("../pages/EnrichmentPage").then(m => ({ default: m.EnrichmentPage })));
const PipelineBoardPage = lazy(() => import("../pages/PipelineBoardPage").then(m => ({ default: m.PipelineBoardPage })));
const PipelineConfigPage = lazy(() => import("../pages/PipelineConfigPage").then(m => ({ default: m.PipelineConfigPage })));

// Phase 5: Outreach & Analytics pages
const SequencesPage = lazy(() => import("../pages/SequencesPage").then(m => ({ default: m.SequencesPage })));
const DeliverabilityPage = lazy(() => import("../pages/DeliverabilityPage").then(m => ({ default: m.DeliverabilityPage })));
const CampaignReportsPage = lazy(() => import("../pages/CampaignReportsPage").then(m => ({ default: m.CampaignReportsPage })));
const ActivityFeedPage = lazy(() => import("../pages/ActivityFeedPage").then(m => ({ default: m.ActivityFeedPage })));
const InvestorOutreachPage = lazy(() => import("../pages/InvestorOutreachPage").then(m => ({ default: m.InvestorOutreachPage })));
// PROJECT-064 T0402 — the warm-intro paths panel. It takes `?target=<investor entity id>` and is
// reached from an investor row, so it is a DEEP-LINK destination rather than a nav row: opened
// without a target it can only say "open this from an investor", which is not a useful landing.
const WarmIntroPathsPage = lazy(() => import("../pages/WarmIntroPathsPage").then(m => ({ default: m.WarmIntroPathsPage })));
const HarnessHomePage = lazy(() => import("../pages/HarnessHomePage").then(m => ({ default: m.HarnessHomePage })));
const HarnessEntityReviewPage = lazy(() => import("../pages/HarnessEntityReviewPage").then(m => ({ default: m.HarnessEntityReviewPage })));
// P218 T218-011 — THE DOOR TO THE BUILD. The console that starts a company setup had no reachable
// route at all (its only host, `EntityHierarchyPage`, is imported by nothing), so @P160's own
// component was absent from every served bundle. This page renders it FOR A NAMED COMPANY.
const EntitySetupPage = lazy(() => import("../pages/EntitySetupPage").then(m => ({ default: m.EntitySetupPage })));
// P280 T0201/T0203 — the pitch preparation surface. It is the HOST the gates were waiting for: the
// BE gates and `PitchPreparationGate` were both built and verified, and nothing mounted them.
const PitchPreparePage = lazy(() => import("../pages/PitchPreparePage").then(m => ({ default: m.PitchPreparePage })));
// T0501 — the deliverables menu. Its OWN page rather than a tab, because a menu that states prices
// must be reachable without passing a gate: preparation gates what we CHECK, not what we may offer.
const PitchDeliverablesPage = lazy(() => import("../pages/PitchDeliverablesPage").then(m => ({ default: m.PitchDeliverablesPage })));
const PitchComparisonPage = lazy(() => import("../pages/PitchComparisonPage").then(m => ({ default: m.PitchComparisonPage })));
const HarnessAuditPage = lazy(() => import("../pages/HarnessAuditPage").then(m => ({ default: m.HarnessAuditPage })));
const HarnessPacingPage = lazy(() => import("../pages/HarnessPacingPage").then(m => ({ default: m.HarnessPacingPage })));
const HarnessRoomPage = lazy(() => import("../pages/HarnessRoomPage").then(m => ({ default: m.HarnessRoomPage })));
const HarnessKpisPage = lazy(() => import("../pages/HarnessKpisPage").then(m => ({ default: m.HarnessKpisPage })));

// Phase 6: Skill Catalog
const SkillCatalogPage = lazy(() => import("../pages/SkillCatalogPage").then(m => ({ default: m.SkillCatalogPage })));
// T0107 (PROJECT-076): admin triage for dev_requests opened when a skill can't be found
const DevelopmentRequestsPage = lazy(() => import("../pages/DevelopmentRequestsPage").then(m => ({ default: m.DevelopmentRequestsPage })));
// P098 T0422: one search across every registry, so an admin can bring a skill into the catalog
const SkillImportPage = lazy(() => import("../pages/SkillImportPage").then(m => ({ default: m.SkillImportPage })));

// Upload Anything
const UploadAnythingPage = lazy(() => import("../pages/UploadAnythingPage").then(m => ({ default: m.UploadAnythingPage })));

// PROJECT-287 — the DOCUMENT SURFACE. It renders a document raisy already holds: the other half of
// "bring something in". The asset is `@/components/document-surface`, and this page is its only
// route. There is deliberately NO documents LIST here — that is a separate item with its own
// approval (§D35), and a page that also listed documents would be it, built sideways.
const DocumentSurfacePage = lazy(() => import("../pages/DocumentSurfacePage").then(m => ({ default: m.DocumentSurfacePage })));

// PROJECT-287 IN-287-25 — THE DOCUMENTS LIST. The surface above reads ONE document; this is how a
// person FINDS one. It was reachable only by typing an address, and a document you cannot find does
// not exist — the requirement the PO turned on by name. It carries the row `My Documents` in MY
// WORKSPACE, and it shows no `BD`/`BDSUB`: the columns are Category and Sub Category in the index's
// own words, because the user does not read coordinates.
const DocumentsPage = lazy(() => import("../pages/DocumentsPage").then(m => ({ default: m.DocumentsPage })));

// Phase 6: Settings pages
const IntegrationsPage = lazy(() => import("../pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const GoogleOAuthCallbackPageLazy = lazy(() => import("../pages/GoogleOAuthCallbackPage").then(m => ({ default: m.GoogleOAuthCallbackPage })));
const LinkedInOAuthCallbackPageLazy = lazy(() => import("../pages/LinkedInOAuthCallbackPage").then(m => ({ default: m.LinkedInOAuthCallbackPage })));
const SendingAccountsPage = lazy(() => import("../pages/SendingAccountsPage").then(m => ({ default: m.SendingAccountsPage })));
const CompliancePage = lazy(() => import("../pages/CompliancePage").then(m => ({ default: m.CompliancePage })));
const TeamPage = lazy(() => import("../pages/TeamPage").then(m => ({ default: m.TeamPage })));
const ProjectsPage = lazy(() => import("../pages/ProjectsPage").then(m => ({ default: m.ProjectsPage })));

// Phase 4: Connections
const ConnectionsPage = lazy(() => import("../pages/ConnectionsPage").then(m => ({ default: m.ConnectionsPage })));

const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
    <Spin size="large" />
  </div>
);
import {
  authProvider as fakeAuthProvider,
  dataProvider as fakeDataProvider,
} from "@/providers/fakerest";
import { ProfilePage } from "../settings/ProfilePage";
import { AdminSettingsPage } from "../settings/AdminSettingsPage";
import { UserSettingsPage } from "../settings/UserSettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "@/providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "@/providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { ForgotPasswordPage } from "../login/ForgotPasswordPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { LandingPage } from "../../public/pages/LandingPage";
import { AboutPage } from "../../public/pages/AboutPage";
import { TelephonyPage } from "../../public/pages/TelephonyPage";
import { CompareHubPage } from "../../public/pages/CompareHubPage";
import { CompareRoute } from "../../public/pages/ComparePage";
import { SharedReviewRoute } from "../../public/pages/SharedReviewPage";
import { PitchDeckPage } from "../../public/pages/PitchDeckPage";
import { PitchDeckV2Page } from "../../public/pitchdeck/PitchDeckV2Page";
import { PitchDeckRoute } from "../../public/pitchdeck/PitchDeckRoute";
import { PrivacyPolicyPage } from "../../public/pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "../../public/pages/TermsOfServicePage";
import { PaymentSuccessPage } from "../../public/pages/PaymentSuccessPage";
import { CookiePolicyPage } from "../../public/pages/CookiePolicyPage";
import { DmcaPage } from "../../public/pages/DmcaPage";
import { AccessibilityPage } from "../../public/pages/AccessibilityPage";
import { ContactPage } from "../../public/pages/ContactPage";
import { BlogPage } from "../../public/pages/BlogPage";
import { BlogPostPage } from "../../public/pages/BlogPostPage";
import { BlogComparePage } from "../../public/pages/BlogComparePage";
import { PricingPage } from "../../public/pages/PricingPage";

const defaultStore = localStorageStore(undefined, "CRM");

// Stub pages for routes we don't need yet
const StubPage = () => <div style={{ padding: 20 }}>Coming soon</div>;
const SetPasswordPage = Object.assign(StubPage, { path: "/set-password" });
const OAuthConsentPage = Object.assign(StubPage, { path: "/oauth-consent" });
const GoogleOAuthCallbackPage = Object.assign(GoogleOAuthCallbackPageLazy, { path: "/integrations/google/callback" });
const LinkedInOAuthCallbackPage = Object.assign(LinkedInOAuthCallbackPageLazy, { path: "/integrations/linkedin/callback" });

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = fakeDataProvider,
  authProvider = fakeAuthProvider,
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry = true,
  ...rest
}: CRMProps) => {
  // Seed the store with CRM prop values if not already stored
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies Partial<ConfigurationContextValue> as ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const start = performance.now();
        logger.info("[CRM][wrappedLogin_010] start");
        const result = await authProvider.login(params);
        logger.info("[CRM][wrappedLogin_020] authProvider.login done", { duration_ms: Math.round(performance.now() - start) });
        try {
          const configStart = performance.now();
          logger.info("[CRM][wrappedLogin_030] fetching configuration");
          const config = await dataProvider.getConfiguration();
          logger.info("[CRM][wrappedLogin_040] configuration fetched", { duration_ms: Math.round(performance.now() - configStart), has_config: Object.keys(config).length > 0 });
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch (err) {
          logger.warn("[CRM][wrappedLogin_050] configuration fetch failed", { error: (err as Error).message });
        }
        logger.info("[CRM][wrappedLogin_100] complete", { duration_ms: Math.round(performance.now() - start) });
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  // Must be memoized: a fresh component reference on every CRM render (e.g. from
  // the useIsMobile transition on first paint, or any other re-render) makes React
  // treat it as a brand-new component type and fully unmount+remount the entire
  // Admin subtree (QueryClient, CoreAdminRoutes, auth check) from scratch every
  // time — this was the real mechanism behind the repeated checkAuth calls /
  // visible double-load. See project340.
  const ResponsiveAdmin = useMemo(
    () => (isMobile ? MobileAdmin : DesktopAdmin),
    [isMobile],
  );

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      loading={RaisyLoadingPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
        dashboard={props.dashboard ?? (() => <Navigate to="/outreach" replace />)}
      {...props}
    >
      <CustomRoutes noLayout>
        {/* Public pages (no auth required) */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/telephony" element={<TelephonyPage />} />
        <Route path="/compare" element={<CompareHubPage />} />
        <Route path="/compare/:slug" element={<CompareRoute />} />
        {/* @P280 T0301 · threat T-3 — the OUTSIDE reader's door. An investor opens a link and
            reads ONE artefact; there is no account and no session. Declared in the public group
            of BOTH shells for the reason D-117 already paid for ("a signed-out buyer's screen
            has no shell") — a route in one shell only is a link that dead-ends on the other. */}
        <Route path="/review/:token" element={<SharedReviewRoute />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/blog-compare" element={<BlogComparePage />} />
        {/* D-084: the public pricing surface. A stranger who reads the ladder can now act on it —
            his chosen plan is carried through sign-up (see the page's own note). */}
        <Route path="/pricing" element={<PricingPage />} />
        {/* Where a Stripe payment sends the buyer back. DECLARED IN BOTH SHELLS, and that is the whole
            point of D-117: this route existed ONLY in the mobile block below, so on a desktop the URL
            matched nothing, React Admin fell back to its empty dashboard, and a paying customer was
            shown the app chrome with nothing in it (the PO, 2026-09-26: "still shows nothing!"). The
            public list is not a per-shell preference — a signed-out buyer's screen has no shell. */}
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/pitch" element={<PitchDeckPage />} />
        <Route path="/pitchdeck" element={<PitchDeckV2Page />} />
        <Route path="/pitchdeck/:deckId" element={<PitchDeckRoute />} />
        {/* D-060: the big-vision deck has its own top-level URL as well. */}
        <Route path="/pitchdeck-big-vision" element={<PitchDeckV2Page version="v2026.09.2" />} />
        {/* D-061: and so does the story deck (hammer + formula). */}
        <Route path="/pitchdeck-story" element={<PitchDeckV2Page version="v2026.09.3" />} />
        <Route path="/privacy_policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms_of_service" element={<TermsOfServicePage />} />
        <Route path="/cookie_policy" element={<CookiePolicyPage />} />
        <Route path="/dmca" element={<DmcaPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Auth pages */}
        <Route path={RequestAccessPage.path} element={<RequestAccessPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path={GoogleOAuthCallbackPage.path} element={<Suspense fallback={<PageLoader />}><GoogleOAuthCallbackPage /></Suspense>} />
        <Route path={LinkedInOAuthCallbackPage.path} element={<Suspense fallback={<PageLoader />}><LinkedInOAuthCallbackPage /></Suspense>} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        {/* P206 T0202 — `/settings` is the PERSON's own page now: the ten settings that decide how
            Raisy behaves for him (D-206-001 · D-206-010). T0200 had moved the taxonomy editor under
            `/admin/*` and left `/settings` forwarding there; that forward is gone, because the
            address it forwarded to is no longer the only settings that exist.
            ⚠️ The admin surface KEEPS its home behind the mode guard, and `AdminSettingsPage.path`
            names that home ITSELF (`/admin/settings`). While the constant still read `/settings`,
            one address meant two pages — which is how a typed URL reached an admin surface the rail
            had correctly hidden (RULES_NAMING §4, and the PO's own finding). */}
        <Route path={UserSettingsPage.path} element={<UserSettingsPage />} />
        <Route path={AdminSettingsPage.path} element={<AdminModeRoute><AdminSettingsPage /></AdminModeRoute>} />
        {/* PROJECT-111A T0844 — the kernel-fix review inbox. Authenticated block:
            a run names kernels and entities, so it is never a public route. */}
        <Route path={KernelRunsPage.path} element={<Suspense fallback={<PageLoader />}><KernelRunsPage /></Suspense>} />
        <Route path="/automation" element={<Suspense fallback={<PageLoader />}><AutomationPage /></Suspense>} />
        <Route path="/campaigns" element={<Suspense fallback={<PageLoader />}><CampaignsPage /></Suspense>} />
        <Route path="/analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>} />
        <Route path="/journeys" element={<Suspense fallback={<PageLoader />}><JourneysPage /></Suspense>} />
        <Route path="/messages" element={<Suspense fallback={<PageLoader />}><EmailPage /></Suspense>} />
        <Route path="/email" element={<Navigate to="/messages" replace />} />
        <Route path="/linkedin-messages" element={<ChannelComingSoon channel="LinkedIn" />} />
        {/* P204 WhatsApp board — the third channel on the Messages domain. R2-24 keeps it registered
            on BOTH shells for the reason it was registered on both in the first place (a route that
            exists on one shell only is invisible at <768px), but it now renders the LOCKED-CHANNEL
            statement rather than the board: the engine is not running, and mounting the board is
            what produced the error, the reload, and "The WhatsApp engine is not answering". */}
        <Route path="/whatsapp-messages" element={<ChannelComingSoon channel="WhatsApp" />} />
        <Route path="/templates" element={<Suspense fallback={<PageLoader />}><TemplatesPage /></Suspense>} />
        <Route path="/segments" element={<Suspense fallback={<PageLoader />}><SegmentsPage /></Suspense>} />
        <Route path="/lists" element={<Suspense fallback={<PageLoader />}><ListsPage /></Suspense>} />
        <Route path="/workflows" element={<Suspense fallback={<PageLoader />}><WorkflowsPage /></Suspense>} />
        {/* Phase 2: AI Agent pages */}
        <Route path="/agent-hub" element={<Suspense fallback={<PageLoader />}><AgentHubPage /></Suspense>} />
        <Route path="/agent-config" element={<Suspense fallback={<PageLoader />}><AgentConfigPage /></Suspense>} />
        <Route path="/process-manager" element={<Suspense fallback={<PageLoader />}><ProcessManagerPage /></Suspense>} />
        <Route path="/process-builder/:processKey" element={<Suspense fallback={<PageLoader />}><ProcessBuilderPage /></Suspense>} />
        <Route path="/entities" element={<Suspense fallback={<PageLoader />}><EntityListPage defaultOwnership={["own", "unknown"]} /></Suspense>} />
        <Route path="/entities/:entity_id" element={<Suspense fallback={<PageLoader />}><EntityDetailPage /></Suspense>} />
        {/* P218 T218-011 — the company's own setup console, reachable by URL for a named company
            (`?entity=`), which is how the doorway's hand-over points at it. Registered on BOTH
            shells (RULES_FE_QA learning 10/28: a route on only one shell silently falls back). */}
        <Route path="/entity-setup" element={<Suspense fallback={<PageLoader />}><EntitySetupPage /></Suspense>} />
        <Route path="/pitch/prepare" element={<Suspense fallback={<PageLoader />}><PitchPreparePage /></Suspense>} />
        <Route path="/pitch/deliverables" element={<Suspense fallback={<PageLoader />}><PitchDeliverablesPage /></Suspense>} />
        {/* @P280 T0311 — the COMPARISON: N pinned texts under ONE evaluation set. The verdict is
            the engine's own sentence (lexicographic, with its declared no-difference band); the profiles
            are evidence beside it, never the answer. */}
        <Route path="/pitch/comparison/:id" element={<Suspense fallback={<PageLoader />}><PitchComparisonPage /></Suspense>} />
          <Route path="/pitch/deliverables" element={<Suspense fallback={<PageLoader />}><PitchDeliverablesPage /></Suspense>} />
          <Route path="/pitch/comparison/:id" element={<Suspense fallback={<PageLoader />}><PitchComparisonPage /></Suspense>} />
        {/* The CRM surface the sidebar links to (PO 2026-09-21). It sits BEFORE the two legacy
            routes below so the combination is the canonical entry point.
            ⚠️ 2026-09-25 — THE LEGACY TWO ARE NOW ADMIN-MODE ONLY (PO ruling: "they are bundled now
            in /crm ... keep them for accessing only via admin mode, but not for user mode").
            They still render and are still reachable by URL — and they remain the one-line revert to
            the split tabs — but only behind the admin-mode switch. A user typing `/contacts` is sent
            to `/outreach` by `AdminModeRoute`, which redirects rather than refusing: a "you are not
            allowed" screen teaches the person that the product has doors he may not open
            (P206 D-206-009). The merged `/crm` above is untouched and remains the user's entry. */}
        <Route path="/crm" element={<Suspense fallback={<PageLoader />}><CrmPage /></Suspense>} />
        {/* CRM sidebar Contacts/Companies — real entity data, overriding the fakerest resources.
            ADMIN-MODE ONLY since 2026-09-25; see the note above. */}
        <Route path="/contacts" element={<AdminModeRoute><Suspense fallback={<PageLoader />}><ContactsPage /></Suspense></AdminModeRoute>} />
        <Route path="/companies" element={<AdminModeRoute><Suspense fallback={<PageLoader />}><CompaniesPage /></Suspense></AdminModeRoute>} />
        <Route path="/ecosystem" element={<Suspense fallback={<PageLoader />}><EntityDetailPage /></Suspense>} />
        <Route path="/ecosystem/:focus_entity_id" element={<Suspense fallback={<PageLoader />}><EntityDetailPage /></Suspense>} />
        <Route path="/kernel-semantic-search" element={<Suspense fallback={<PageLoader />}><KernelSemanticSearchPage /></Suspense>} />
        {/* ⚠️ THE PO RENAMED THIS SURFACE (`IN-287-25`): *"PLZ rename to Documents"*. The old address
            stays alive as a REDIRECT rather than 404ing a URL somebody has open (P080 D-125: a
            rename must not 404), and the coordinate lookup keeps an address of its own below — an
            operator's debug tool with no nav row, because `BD` is not a word the user reads. */}
        <Route path="/kernel-exact-match" element={<RedirectKeepingQuery to="/documents" />} />
        <Route path="/kernel-exact-match/lookup" element={<Suspense fallback={<PageLoader />}><KernelExactMatchPage /></Suspense>} />
        <Route path="/entity-semantic-search" element={<Suspense fallback={<PageLoader />}><EntitySemanticSearchPage /></Suspense>} />
        <Route path="/entity-exact-match" element={<Suspense fallback={<PageLoader />}><EntityExactMatchPage /></Suspense>} />
        <Route path="/agent-activity" element={<Suspense fallback={<PageLoader />}><AgentActivityPage /></Suspense>} />
        <Route path="/proposals" element={<Suspense fallback={<PageLoader />}><ProposalsPage /></Suspense>} />
        {/* P098 T0428 — THE GUARD THE THREE SKILL SURFACES WERE MISSING.
            All three sit in nav groups the sidebar itself marks `adminOnly`, and all three were
            reachable by TYPING the URL — a hidden link is not a locked door, which is the sentence
            `AdminOnlyRoute` exists for.

            ⚠️ AND WHAT THIS ROW GOT WRONG, RECORDED BECAUSE IT CHANGES THE FIX. It was filed as
            *"a signed-out visitor reaches it and sees a full shell that then 401s"*. That is FALSE.
            `requireAuth` + `checkAuth` already send a stranger to `/login` — proven at 1440×900 and
            390×844 for these three AND for the two routes the project considers correctly gated
            (`tests/e2e/probe_admin_route_gate.mjs`). The arm that was actually open is the
            **signed-in non-administrator**: `checkAuth` passes for ANY session, so he got a fully
            rendered shell whose every call the server refused — *"a page that looks real and answers
            nothing"*, which is the defect the row is about, reached by the other visitor. The fix is
            therefore this file's EXISTING guard, not a second gate invented for one row.

            SCOPE, MEASURED RATHER THAN ARGUED. `node scripts/check_admin_route_gate.mjs` reports how
            many admin-only nav rows have an unguarded route: **38 of 39**, before this change. Most
            are desktop-only registrations (already filed as T0426) and gating each is a product call.
            These three are not: they are one surface family, they were unguarded on BOTH shells, and
            the sidebar's own `adminOnly` already answers the question. The rest stay recorded, not
            silently gated. */}
        <Route path="/skills" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><SkillCatalogPage /></Suspense></AdminOnlyRoute>} />
        <Route path="/development-requests" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><DevelopmentRequestsPage /></Suspense></AdminOnlyRoute>} />
        <Route path="/skill-import" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><SkillImportPage /></Suspense></AdminOnlyRoute>} />
        <Route path="/upload-anything" element={<Suspense fallback={<PageLoader />}><UploadAnythingPage /></Suspense>} />
        {/* PROJECT-287 — one document, rendered and editable in place (`T0287.3`). */}
        {/* PROJECT-287 IN-287-25 — THE DOCUMENTS LIST, beside the surface it opens. */}
        <Route path="/documents" element={<Suspense fallback={<PageLoader />}><DocumentsPage /></Suspense>} />
        <Route path="/documents/:kernelKey" element={<Suspense fallback={<PageLoader />}><DocumentSurfacePage /></Suspense>} />
        {/* Phase 3: Feedback pages */}
        <Route path="/feedback-queue" element={<Suspense fallback={<PageLoader />}><FeedbackQueuePage /></Suspense>} />
        <Route path="/feedback-history" element={<Suspense fallback={<PageLoader />}><FeedbackHistoryPage /></Suspense>} />
        {/* Phase 4: Data & Pipeline pages */}
        <Route path="/prospecting" element={<Suspense fallback={<PageLoader />}><ProspectingPage /></Suspense>} />
        <Route path="/enrichment" element={<Suspense fallback={<PageLoader />}><EnrichmentPage /></Suspense>} />
        <Route path="/pipeline-board" element={<Suspense fallback={<PageLoader />}><PipelineBoardPage /></Suspense>} />
        <Route path="/pipeline-config" element={<Suspense fallback={<PageLoader />}><PipelineConfigPage /></Suspense>} />
        {/* Phase 5: Outreach & Analytics pages */}
        <Route path="/sequences" element={<Suspense fallback={<PageLoader />}><SequencesPage /></Suspense>} />
        <Route path="/deliverability" element={<Suspense fallback={<PageLoader />}><DeliverabilityPage /></Suspense>} />
        <Route path="/campaign-reports" element={<Suspense fallback={<PageLoader />}><CampaignReportsPage /></Suspense>} />
        <Route path="/activity-feed" element={<Suspense fallback={<PageLoader />}><ActivityFeedPage /></Suspense>} />
          <Route path="/outreach" element={<Suspense fallback={<PageLoader />}><InvestorOutreachPage /></Suspense>} />
          {/* The old URL stays ALIVE and keeps its query (P080 D-125): bookmarks and the entity
              deep links from EntityDetailPage must land on the equity raise they pointed at. */}
          <Route path="/investor-outreach" element={<RedirectKeepingQuery to="/outreach" />} />
        {/* PROJECT-064 T0402 — the warm-intro paths panel: who in MY network can introduce me to
            the investor in `?target=`. Registered in BOTH shells on purpose — a route in only one
            does not 404, it silently renders the dashboard and looks healthy (@RULES_FE_QA
            learning 10/28). */}
        <Route path="/warm-intro-paths" element={<Suspense fallback={<PageLoader />}><WarmIntroPathsPage /></Suspense>} />
        {/* project139 placeholder — Pillar 3 Meetings surface. */}
        <Route path="/meetings" element={<Suspense fallback={<PageLoader />}><MeetingsPage /></Suspense>} />
        {/* Pillar 4's placeholder — the fourth link, visible in user mode without pretending to be built. */}
        <Route path="/contracts" element={<Suspense fallback={<PageLoader />}><ContractsPage /></Suspense>} />
        {/* project127 T101/T102 — Harness (founder/operator orchestration surface, D-006/D-007) */}
        {/* ADMIN-ONLY (PO 2026-09-16). The sidebar's `adminOnly` HIDES the nav item;
            a hidden link is not access control, so the routes are wrapped here too.
            Anyone without admin who types the URL is redirected. */}
        <Route path="/harness" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessHomePage /></Suspense></AdminOnlyRoute>} />
        {/* P144 T0406 — the surface the red dot leads to is `Entity Review`, and ITS ADDRESS IS
            `/entity-review`, not `/harness/entity-review` (PO 2026-10-05, P042 `D-042-016`):
            `/harness/*` is the OPERATOR's area and holds harness surfaces only, so a surface its
            owner reaches in user mode must not live inside it. The former path and the retired name
            `Decide`/`/harness/decide` stay ALIVE as redirects that KEEP the `?entity=` deep link:
            the backend's wake lanes and every bookmark point at the old URLs, and a rename may lose
            the NAME, never the state the old URL carried (P080 D-125). */}
        {/* P144 D-144-015 (2026-09-27) — `Entity Review` is NOT wrapped in `AdminOnlyRoute`:
            the persona gate decided it (the canonical persona file puts the customer in this
            surface, and a dot that announces work must be reachable by whoever can see it).
            The rest of `/harness/*` keeps its gate, and the operator's command bar stays behind
            admin mode INSIDE the page — opening the queue must not smuggle the command line. */}
        <Route path="/entity-review" element={<Suspense fallback={<PageLoader />}><HarnessEntityReviewPage /></Suspense>} />
        <Route path="/harness/entity-review" element={<RedirectKeepingQuery to="/entity-review" />} />
        <Route path="/harness/decide" element={<RedirectKeepingQuery to="/entity-review" />} />
        <Route path="/harness/audit" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessAuditPage /></Suspense></AdminOnlyRoute>} />
        <Route path="/harness/pacing" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessPacingPage /></Suspense></AdminOnlyRoute>} />
        <Route path="/harness/room" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessRoomPage /></Suspense></AdminOnlyRoute>} />
        {/* P136 T0189 / D-015 — the KPI surface the product never had. Nothing imported the KPI
            components before this route: the numbers were written, served, and shown nowhere.
            P042 `D-042-014` moved the ROW to MY WORKSPACE; `D-042-016` (PO 2026-10-05) moved the
            ADDRESS with it — `/kpis`, outside `/harness/*`, because the numbers are the OWNER's and
            the namespace is the operator's. Two things were wrong until this pass: the path still
            named the operator's area, and the route was still wrapped in `AdminOnlyRoute`, so the
            row `D-042-014` made visible sent a non-admin to `/outreach` when he clicked it. The
            gate is gone for the same reason it is gone from `Entity Review` (D-144-015). */}
        <Route path="/kpis" element={<Suspense fallback={<PageLoader />}><HarnessKpisPage /></Suspense>} />
        <Route path="/harness/kpis" element={<RedirectKeepingQuery to="/kpis" />} />
        {/* re-parent: Company Console v1 is re-homed as Harness Home (D-007) —
            old URL redirects. Guarded too: otherwise it is an unguarded door onto
            a guarded page, which reads as a bypass even though the target re-checks. */}
          <Route path="/company-console" element={isAdministrator() ? <Navigate to="/harness" replace /> : <Navigate to="/outreach" replace />} />
        {/* Phase 6: Settings pages */}
        <Route path="/integrations" element={<Suspense fallback={<PageLoader />}><IntegrationsPage /></Suspense>} />
        <Route path="/sending-accounts" element={<Suspense fallback={<PageLoader />}><SendingAccountsPage /></Suspense>} />
        <Route path="/compliance" element={<Suspense fallback={<PageLoader />}><CompliancePage /></Suspense>} />
        <Route path="/team" element={<Suspense fallback={<PageLoader />}><TeamPage /></Suspense>} />
        <Route path="/projects" element={<Suspense fallback={<PageLoader />}><ProjectsPage /></Suspense>} />
        <Route path="/connections" element={<Suspense fallback={<PageLoader />}><ConnectionsPage /></Suspense>} />
        {/* @P091 T0112 — the credit ledger's admin surface. GUARDED here, not by the nav flag:
            the sidebar only hides the LINK, and a typed URL still resolved (see AdminOnlyRoute). */}
        <Route path="/customer-credits" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><CustomerCreditsPage /></Suspense></AdminOnlyRoute>} />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      {/* ⚠️ THE `sales` RESOURCE IS NO LONGER REGISTERED (P206 D-206-013, PO 2026-09-22:
          *"MOCKUP, for sure remove!"*). It rendered the inherited demo CRM's list of five generated
          people — the mockup the PO met in the top-right menu (IN-05: *"i see weird users, who are
          those ? mockup ? why would i see them there ?"*). The menu row is gone with it, so the list
          is reachable by neither a link nor a typed URL.

          WHAT DELIBERATELY STAYS: the files (`crm/sales/*`, the fakerest generator) and the
          DATA-PROVIDER reads that were never routes — `useGetSalesName` (the activity feed) and row
          7's account block both call `getOne("sales")` directly, which a `<Resource>` registration
          never provided. **A ROUTE IS NOT A DATA SOURCE**, so retiring the route cannot break a
          reader — verified on the deployed DEV FE before this was called done.

          The precedent was searched for and applied deliberately (RULES_DECISIONS §3j.2): P013 D-006
          retired `/import` the same way — remove the route, keep the files on disk. */}
      <Resource name="tags" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  // Memoized: creating a new QueryClient on every render discards its cache and
  // observers each time (including the checkAuth query from useAuthState),
  // forcing a refetch on every re-render instead of once per mount. See project340.
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            gcTime: 1000 * 60 * 60 * 24,
            networkMode: "offlineFirst",
          },
          mutations: {
            networkMode: "offlineFirst",
          },
        },
      }),
    [],
  );
  const asyncStoragePersister = useMemo(
    () => createAsyncStoragePersister({ storage: localStorage }),
    [],
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        dehydrateOptions: {
          // THE ATTENTION COUNT IS NOT PERSISTED, and that is a rule rather than an oversight
          // (P144 D-144-001 · D-144-009, and the badge's own honesty rule). This client persists
          // its cache to localStorage, so without this filter a reload would rehydrate a
          // portfolio count of unknown age and show it as current — a number the user acts on,
          // presented as a fact. The dot is either REAL and fresh, or the first fetch has not
          // landed yet and it is ABSENT. Every other query keeps the persister's default.
          shouldDehydrateQuery: (query) => query.queryKey[0] !== ATTENTION_QUERY_KEY[0],
        },
      }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          {/* Public pages (no auth required) — THE SAME SET AS THE DESKTOP BLOCK ABOVE. A public page
              declared in one shell only renders as an empty dashboard in the other (D-117: that is
              exactly what happened to /payment-success). `scripts/check_shell_parity.mjs` enforces it. */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/telephony" element={<TelephonyPage />} />
          <Route path="/compare" element={<CompareHubPage />} />
          <Route path="/compare/:slug" element={<CompareRoute />} />
          {/* @P280 T0301 · threat T-3 — the OUTSIDE reader's door. An investor opens a link and
              reads ONE artefact; there is no account and no session. Declared in the public group
              of BOTH shells for the reason D-117 already paid for ("a signed-out buyer's screen
              has no shell") — a route in one shell only is a link that dead-ends on the other. */}
          <Route path="/review/:token" element={<SharedReviewRoute />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/blog-compare" element={<BlogComparePage />} />
          {/* D-084: the public pricing surface — the same route as the desktop block above. */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* Where a Stripe Payment Link sends the buyer after paying (glowtest_FE's /payment-success
              pattern, with the value taken out of the URL — see the page's own note). */}
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/pitch" element={<PitchDeckPage />} />
          <Route path="/pitchdeck" element={<PitchDeckV2Page />} />
          <Route path="/pitchdeck/:deckId" element={<PitchDeckRoute />} />
          <Route path="/pitchdeck-big-vision" element={<PitchDeckV2Page version="v2026.09.2" />} />
          <Route path="/pitchdeck-story" element={<PitchDeckV2Page version="v2026.09.3" />} />
          <Route path="/privacy_policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms_of_service" element={<TermsOfServicePage />} />
          <Route path="/cookie_policy" element={<CookiePolicyPage />} />
          <Route path="/dmca" element={<DmcaPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path={RequestAccessPage.path} element={<RequestAccessPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path={GoogleOAuthCallbackPage.path} element={<Suspense fallback={<PageLoader />}><GoogleOAuthCallbackPage /></Suspense>} />
        <Route path={LinkedInOAuthCallbackPage.path} element={<Suspense fallback={<PageLoader />}><LinkedInOAuthCallbackPage /></Suspense>} />
        </CustomRoutes>
        <CustomRoutes>
          {/* P206 T0206 — ONE URL, ONE PRODUCT. This address used to answer with `SettingsPageMobile`
              on this shell and the taxonomy editor on the desktop one, so a person on a phone never
              met the ten settings and a person on a desktop never met his own sign-in and appearance.
              Both shells now render the SAME tree, and what the mobile page held is kept — it is
              mounted inside row 7 ("My profile") as `AccountSection`. */}
          <Route path={UserSettingsPage.path} element={<UserSettingsPage />} />
          {/* P206 T0200 — the SAME admin surface as the desktop route above, registered on THIS
              shell too: a route present on only one shell silently falls back to the dashboard
              (P080 lesson). */}
          <Route path={AdminSettingsPage.path} element={<AdminModeRoute><AdminSettingsPage /></AdminModeRoute>} />
          {/* ⚠️ `/profile` WAS MISSING FROM THIS SHELL ENTIRELY, AND THE WALK FOUND IT (P206 T0700).
              The desktop block registers it; this one did not — so a person on a phone who reached
              `/profile` (from the avatar menu, or the daily-jobs mail) got a page with no heading and
              no contents, while the same URL on a desktop rendered his entity. Measured: the walk's
              W3 passed on both desktop contexts and failed on BOTH mobile ones with `heading=null`.
              This is the file's own recorded lesson, two comments above, applied to a route that had
              escaped it: a route registered on only ONE shell is invisible on the other. */}
          <Route path={ProfilePage.path} element={<ProfilePage />} />
          {/* Real entity list — also the redirect target for the fake-CRM guard in MobileLayout.
              Seeded to `["own", "unknown"]` — everything that is NOT external — because `My
              Entities` is the tab holding your own side of the graph and the CRM is the external
              half. Matches the desktop route above; a route registered on only ONE shell is
              invisible on the other (RULES_FE_QA learning 10/28). */}
          <Route path="/entities" element={<Suspense fallback={<PageLoader />}><EntityListPage defaultOwnership={["own", "unknown"]} /></Suspense>} />
          {/* The CRM surface, reachable under the mobile shell too (RULES_FE_QA learning 28:
              a route that exists in only ONE shell silently falls back to the dashboard, so the
              rail would advertise a surface the mobile router cannot serve). */}
          <Route path="/crm" element={<Suspense fallback={<PageLoader />}><CrmPage /></Suspense>} />
          {/* P218 T218-011 — the same door on the mobile shell: the hand-over links to it from
              `/messages`, which is a route both shells serve. */}
          <Route path="/entity-setup" element={<Suspense fallback={<PageLoader />}><EntitySetupPage /></Suspense>} />
          <Route path="/pitch/prepare" element={<Suspense fallback={<PageLoader />}><PitchPreparePage /></Suspense>} />
          {/* MR23 founder landing — closes the <768px product gap (RULES_FE_QA learning 10): the
              raises dashboard + wizard are responsive, so route them under the mobile shell too. */}
            <Route path="/outreach" element={<Suspense fallback={<PageLoader />}><InvestorOutreachPage /></Suspense>} />
            <Route path="/investor-outreach" element={<RedirectKeepingQuery to="/outreach" />} />
            {/* @PROJECT-064 T0402 — the SAME panel under the mobile shell (the single-shell defect
                learning 28 measured: a route served by one shell alone renders the dashboard while
                looking healthy). */}
            <Route path="/warm-intro-paths" element={<Suspense fallback={<PageLoader />}><WarmIntroPathsPage /></Suspense>} />
          {/* @P092 T0209 — the analytics read, and the SAME single-shell defect learning 10 and 28
              name. Measured 2026-10-05: `/analytics` was registered in the desktop shell alone, so
              at 390×844 it rendered a BLANK body with zero console errors — not a fallback, not an
              honest sentence, nothing. A founder on a phone could not see her own outreach numbers
              at all. Routed under the mobile shell beside `/outreach`, which is where it belongs:
              the analytics page is the read of the very surface `/outreach` writes to. */}
          <Route path="/analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>} />
          {/* Bring-something-in — reachable under the mobile shell too (RULES_FE_QA learning 28:
              a route that exists in only ONE shell does not 404 — the app silently falls back to
              the dashboard and the page that renders looks healthy while it is the wrong page). */}
          <Route path="/upload-anything" element={<Suspense fallback={<PageLoader />}><UploadAnythingPage /></Suspense>} />
          {/* PROJECT-287 — and UNDER THE MOBILE SHELL TOO, for the reason stated three lines above:
              a route that exists in only one shell does not 404, it renders the dashboard, and the
              page looks healthy while it is the wrong page (RULES_FE_QA learning 28). */}
          {/* PROJECT-287 IN-287-25 — the LIST under the mobile shell too, for learning 28's reason
              (a route on one shell alone does not 404, it renders the dashboard and looks healthy). */}
          <Route path="/documents" element={<Suspense fallback={<PageLoader />}><DocumentsPage /></Suspense>} />
          <Route path="/documents/:kernelKey" element={<Suspense fallback={<PageLoader />}><DocumentSurfacePage /></Suspense>} />
          {/* project127 Harness — reachable under the mobile shell too (learning 10) */}
          {/* P098 T0428 — FIVE GUARDS THE MOBILE TABLE HAD DROPPED (found by
              `scripts/check_admin_route_gate.mjs`, not by reading). Every route below is wrapped in
              `AdminOnlyRoute` on the DESKTOP table and was bare here, so the SAME URL was refused on
              a laptop and served on a phone. This is not a product call — the desktop side already
              made it — which is why these are closed under a row that is nominally about one route.
              The two NOT wrapped are deliberate, and the reason lives on the desktop table
              (D-144-015): `entity-review` is the owner's own queue behind the red dot, and `decide`
              is its retired name kept alive as a redirect.
              ⚠️ BOTH LEFT `/harness/*` ON 2026-10-05 (P042 `D-042-016`): the namespace is the
              operator's area, so the owner's surfaces read `/entity-review` and `/kpis`, and the
              two former addresses stay as redirects below. */}
          <Route path="/harness" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessHomePage /></Suspense></AdminOnlyRoute>} />
          <Route path="/entity-review" element={<Suspense fallback={<PageLoader />}><HarnessEntityReviewPage /></Suspense>} />
          <Route path="/harness/entity-review" element={<RedirectKeepingQuery to="/entity-review" />} />
          <Route path="/harness/decide" element={<RedirectKeepingQuery to="/entity-review" />} />
          <Route path="/harness/audit" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessAuditPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/harness/pacing" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessPacingPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/harness/room" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><HarnessRoomPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/kpis" element={<Suspense fallback={<PageLoader />}><HarnessKpisPage /></Suspense>} />
          <Route path="/harness/kpis" element={<RedirectKeepingQuery to="/kpis" />} />
          {/* P133 email surface — found by FE QA 2026-09-11 (capture_p133_switcher_email.mjs at
              390x844): /email was registered on the DESKTOP shell only, so at <768px the route did
              not exist and the app silently fell back to the mobile dashboard — the same class of
              gap as /investor-outreach above (RULES_FE_QA learning 10). The page is responsive. */}
          <Route path="/messages" element={<Suspense fallback={<PageLoader />}><EmailPage /></Suspense>} />
          <Route path="/email" element={<Navigate to="/messages" replace />} />
            {/* INTEGRATIONS — found by P205 FE QA at 390x844 (2026-09-22): this route was
                registered on the DESKTOP shell only, so below 768px the app silently fell back to
                the mobile dashboard and the LinkedIn Messaging card was UNREACHABLE — not
                unrendered. That card is where this channel's connect step lives.
                RULES_FE_QA learning 10/28, the same class as /email, /meetings and
                /customer-credits. The page is responsive by construction (grid-cols-1 md:2 lg:3). */}
            <Route path="/integrations" element={<Suspense fallback={<PageLoader />}><IntegrationsPage /></Suspense>} />
          <Route path="/linkedin-messages" element={<ChannelComingSoon channel="LinkedIn" />} />
            <Route path="/whatsapp-messages" element={<ChannelComingSoon channel="WhatsApp" />} />          {/* project139 placeholder — Pillar 3 Meetings surface. */}
          <Route path="/meetings" element={<Suspense fallback={<PageLoader />}><MeetingsPage /></Suspense>} />
          <Route path="/contracts" element={<Suspense fallback={<PageLoader />}><ContractsPage /></Suspense>} />
          <Route path="/pipeline-board" element={<Suspense fallback={<PageLoader />}><PipelineBoardPage /></Suspense>} />
        // T0414 (@P210, RULES_FE_QA learning 28): the DESKTOP table carried /pipeline-board and the mobile one
        // did not, so at phone width the app kept the URL and rendered the mobile dashboard instead - a silent
        // fallback with a clean console (measured 2026-09-23: board rendered=false, 0 push / 0 pull). The page was
        // already shared by both shells; only the registration was missing.
          {/* P135 kernel-fix runs — found by FE QA 2026-09-15 (capture_project135_learning_ui.mjs
              at 390x844): the ADMIN → Kernel Runs nav item RENDERS in the mobile rail and the page
              did not, so the link was dead and the shell silently fell back to the dashboard. Same
              class as /email above (RULES_FE_QA learning 10). ⚠️ A dead nav link is worse than an
              absent one: the rail advertises a surface the router cannot serve, and nothing errors
              — the person just lands somewhere else and assumes they mis-tapped. */}
          <Route path={KernelRunsPage.path} element={<Suspense fallback={<PageLoader />}><KernelRunsPage /></Suspense>} />
          {/* @P091 T0112 — the same reason as the note above: the ADMIN → Customer credits nav item
              renders in the mobile rail, so the page has to be servable here too or the link is dead. */}
          <Route path="/customer-credits" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><CustomerCreditsPage /></Suspense></AdminOnlyRoute>} />
          {/* P098 T0425 (2026-09-29) — TWO ROUTES THAT ANSWERED A PHONE WITH NOTHING.
              Measured by the FEQA sweep (2026-09-25, item 8) at 390×844: `/skills` and
              `/development-requests` painted NOTHING — 0 chars, 0 headings, **0 API requests, 0
              console errors** — while both rendered at 1440×900. *Zero requests with zero errors is
              not a failure; it is a page that was never asked for.* Both were registered on the
              DESKTOP table only, so below 768px the router fell back and the console stayed clean.
              This is the seventh instance of this file's recorded lesson — `/email`, `/integrations`,
              `/pipeline-board`, `/profile`, `/customer-credits` and Kernel Runs all arrived here the
              same way — and for these two the fix is REGISTRATION ONLY, because both desktop pages
              are already responsive.
              ⚠️ THE REASON IS *NOT* A DEAD RAIL LINK, and that is worth stating because the Kernel Runs
              and Customer Credits comments above give exactly that reason. It does not hold for the
              current shells: `MobileLayout` renders `MobileNavigation`, a five-slot bottom tab bar
              whose paths are HARDCODED (`/`, `/contacts`, `/tasks`, create, settings), and it draws no
              sidebar — so at <768px the ADMIN group is never advertised and nothing is a link to
              anything. **The harm is different, and worse in one respect: the failure is SILENT.** A
              page that never asks cannot be told apart from a page that is not built, and the sweep
              records the cost precisely — *every mobile row for these surfaces is a FAIL that no work
              on the surface itself can move*. `/skills` is also the surface C21's import page sits
              beside. */}
          {/* P098 T0428 — guarded here too, and the reason is the same one this comment block already
              gives for the registration: a route that differs between shells is a defect. Unguarding
              a page on mobile only would leave the same URL refused on a laptop and served on a phone. */}
          <Route path="/skills" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><SkillCatalogPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/development-requests" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><DevelopmentRequestsPage /></Suspense></AdminOnlyRoute>} />
          {/* ⚠️ REGISTERED ON BOTH SHELLS FROM THE START, and that is C20/T0425's lesson applied
              rather than re-learned: the two routes above spent months on the DESKTOP table only,
              so below the shell switch they rendered NOTHING with a clean console — the seventh
              instance of this file's recorded lesson, fixed the day before this page landed.
              `node scripts/check_route_parity.mjs` reports any page that drifts back. */}
          {/* P098 T0428 — guarded, same as the desktop table above. */}
          <Route path="/skill-import" element={<AdminOnlyRoute><Suspense fallback={<PageLoader />}><SkillImportPage /></Suspense></AdminOnlyRoute>} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
